import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import feedService from '@wlb/shared/api/services/feedService';
import profileService from '@wlb/shared/api/services/profileService';
import { POST_MEDIA_TYPE } from '@wlb/shared/constants';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useFeed } from '@wlb/shared/context/FeedContext';
import type { CommentItem } from '@wlb/shared/types/comments';
import type { FeedPost, MediaFile } from '@wlb/shared/types/post';
import type { GifSelection } from '@wlb/shared/types/gif';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { normalizeUserGuid } from '@wlb/shared/utils/navigateToUserProfile';
import { useCurrentProfile } from '../profile/useCurrentProfile';
import { useOpenUserProfile } from '../profile/useOpenUserProfile';
import { useAuthGate } from '../../components/feedback/AuthGate';
import { isStaticTrendingPost } from './staticPosts';
import {
  CommentIcon, HeartIcon, PostMenuIcon, PencilIcon, TrashIcon,
  ReplyIcon, AlertCircleIcon, BlockIcon, SendIcon, ShareIcon, SmileIcon,
} from '../../components/ui/icons';
import { FormError } from '../../components/ui/FormError';
import { ListStatus } from '../../components/ui/ListStatus';
import { PopoverMenu, type PopoverMenuItem } from '../../components/ui/PopoverMenu';
import { avatarFallback } from '../../components/ui/avatarFallback';
import '../../components/ui/avatar.css';
import { MentionText } from '../../components/ui/MentionLink';
import { Hashtag } from '../../components/ui/Hashtag';
import { GifPicker } from '../../components/ui/GifPicker';
import { HtmlContent } from '../../components/ui/HtmlContent';
import { containsHtml } from '../../utils/sanitizeHtml';
import { useAutoResizeTextarea } from '../../utils/useAutoResizeTextarea';
import { buildMentionIndex, toMentionToken } from '@wlb/shared/utils/mentions';
import {
  applyMentionSelection,
  buildMentionSuggestions,
  filterMentionSuggestions,
  isCaretMoveKey,
  readMentionQuery,
  type MentionQuery,
  type MentionSuggestion,
} from '@wlb/shared/utils/mentionSuggestions';
import { MentionSuggestions } from './MentionSuggestions';
import { splitSeparateHashtags } from '../../utils/separateHashtags';
import { formatApiTimestamp } from '../../utils/apiTimestamp';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from './ConfirmModal';
import { BlockUserModal } from './BlockUserModal';
import { ReportUserModal } from '../profile/ReportUserModal';
import { FeedUserRowsSkeleton } from './FeedModalSkeleton';
import { LikesModal } from './LikesModal';
import { resolvePostAlbum } from './postMedia';
import { canSharePost } from '@wlb/shared/utils/postSharing';
import { resolveReplyParentId } from './commentReply';
import './feedModal.css';

interface CommentsModalProps {
  post: FeedPost;
  onClose: () => void;
  onCommentAdded: () => void;
  /** Fired when the visible comment total changes (add/delete). */
  onCommentCountChange?: (delta: number) => void;
  fullView?: boolean;
  title?: string;
  /** Comment ID to scroll to and highlight (from notification navigation). */
  highlightCommentId?: string | null;
  /** When true, opens a split media-viewer layout (media left, comments right).
   *  Used only for direct image/GIF/video clicks. */
  mediaViewer?: boolean;
  /** Starting image index when opened from a gallery grid click. */
  initialMediaIndex?: number;
  /** Post-level menu (edit / delete / save / report). Same handler as PostCard. */
  onMenu?: (post: FeedPost, anchorRect: DOMRect) => void;
}

const COMMENT_CHAR_LIMIT = 500;
/** Matches the `max-height` on `.comments-modal__composer textarea`. */
const COMPOSER_MAX_HEIGHT = 120;
const MENTION_LISTBOX_ID = 'comments-mention-suggestions';

type CommentMenuKey = 'edit' | 'delete' | 'reply' | 'report' | 'block';

const commenterName = (comment: Pick<CommentItem, 'username'>): string =>
  getUsernameDisplay(comment);

const commenterMentionToken = (comment: Pick<CommentItem, 'username'>): string =>
  toMentionToken(commenterName(comment)) || toMentionToken(comment.username);

/* ─── Emoji data (categorised, mobile-style grid) ────────────────────── */

import { EmojiPicker } from '../../components/ui/EmojiPicker';

/* ─── Component ──────────────────────────────────────────────────────── */

export function CommentsModal({ post, onClose, onCommentAdded, onCommentCountChange, fullView = false, title, highlightCommentId, mediaViewer = false, initialMediaIndex = 0, onMenu }: CommentsModalProps) {
  const { t } = useTranslation();
  const { toggleBlockUser, likePost } = useFeed();
  const [comments, setComments] = useState<CommentItem[]>([]);
  const knownCommentIds = useRef<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [newCommentIds, setNewCommentIds] = useState<Set<string>>(() => new Set());
  const [mediaIndex, setMediaIndex] = useState(initialMediaIndex);

  // Kebab menu
  const [menu, setMenu] = useState<{ comment: CommentItem; anchorRect: DOMRect } | null>(null);

  // Edit
  const [editTarget, setEditTarget] = useState<CommentItem | null>(null);
  const [editText, setEditText] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editedCommentIds, setEditedCommentIds] = useState<Set<string>>(() => new Set());

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<CommentItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Reply
  const [replyTarget, setReplyTarget] = useState<CommentItem | null>(null);

  // @mention autocomplete
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  useAutoResizeTextarea(composerRef, text, { maxHeight: COMPOSER_MAX_HEIGHT });
  const [mentionQuery, setMentionQuery] = useState<MentionQuery | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  /** Caret to restore after an insertion rewrites the composer value. */
  const [pendingCaret, setPendingCaret] = useState<number | null>(null);

  // Hide / show replies
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(() => new Set());

  // Block
  const [blockTarget, setBlockTarget] = useState<{ userId: number; username: string; isBlocked: boolean } | null>(null);
  const [blockLoading, setBlockLoading] = useState(false);

  // Report
  const [reportTargetGuid, setReportTargetGuid] = useState<string | null>(null);

  // Post-level like state (local optimistic toggle)
  const [postLiked, setPostLiked] = useState(post.liked);
  const [postLikeCount, setPostLikeCount] = useState(post.likes);
  const [likesModalOpen, setLikesModalOpen] = useState(false);

  const { userId } = useAuth();
  const currentProfile = useCurrentProfile();
  const openUserProfile = useOpenUserProfile();
  const { requireAuth } = useAuthGate();
  const staticTrendingPost = isStaticTrendingPost(post);

  // Identities @mentions in this thread can resolve to: the post author, the
  // people tagged on it, everyone who commented or replied, and the reader.
  const mentions = useMemo(
    () =>
      buildMentionIndex([
        {
          userGuid: post.profileUserGuid,
          username: post.username,
          displayName: post.username,
        },
        {
          userGuid: currentProfile?.userGuid,
          username: currentProfile?.userName,
          displayName: currentProfile?.userName,
        },
        ...(post.taggedBuddies ?? []).map(buddy => ({
          userGuid: buddy.profileUserGuid,
          username: buddy.name,
          displayName: buddy.name,
        })),
        ...comments.flatMap(comment => [
          {
            userGuid: comment.profileUserGuid,
            username: comment.username,
            displayName: comment.username,
          },
          ...(comment.replies ?? []).map(reply => ({
            userGuid: reply.profileUserGuid,
            username: reply.username,
            displayName: reply.username,
          })),
        ]),
      ], { preferUsername: true }),
    [
      post.profileUserGuid,
      post.username,
      post.taggedBuddies,
      currentProfile,
      comments,
    ],
  );
  /**
   * People the composer can offer for "@". Only this thread's participants:
   * a mention resolves against exactly this set when the comment is rendered,
   * so suggesting anyone else would write a label that links to nobody.
   * Ordered by how likely they are to be meant.
   */
  const mentionCandidates = useMemo(() => {
    const commentAuthors = comments.flatMap(comment => [comment, ...(comment.replies ?? [])]);
    const fromComment = (item: CommentItem) => ({
      userGuid: item.profileUserGuid,
      username: item.username,
      avatar: item.avatar,
    });

    return buildMentionSuggestions(
      [
        replyTarget ? fromComment(replyTarget) : null,
        {
          userGuid: post.profileUserGuid,
          username: post.username,
          avatar: typeof post.avatar === 'string' ? post.avatar : undefined,
        },
        ...commentAuthors.filter(item => !item.isBlocked).map(fromComment),
        ...(post.taggedBuddies ?? []).map(buddy => ({
          userGuid: buddy.profileUserGuid,
          username: buddy.name,
          avatar: typeof buddy.avatar === 'string' ? buddy.avatar : undefined,
        })),
      ],
      currentProfile?.userGuid,
      { preferUsername: true },
    );
  }, [
    comments,
    currentProfile?.userGuid,
    post.avatar,
    post.profileUserGuid,
    post.taggedBuddies,
    post.username,
    replyTarget,
  ]);

  const mentionSuggestions = useMemo(
    () => (mentionQuery ? filterMentionSuggestions(mentionCandidates, mentionQuery.query) : []),
    [mentionCandidates, mentionQuery],
  );

  const videoUri = post.video?.uri ?? post.videoUrl ?? null;
  // The whole album, so a multi-photo post can be paged through here even when
  // the card that opened it showed a single picture.
  const mediaSources = videoUri ? [videoUri] : resolvePostAlbum(post);
  const activeMedia = mediaSources[mediaIndex] ?? null;

  /**
   * Natural ratio of the media on screen. The viewer scales the media to the
   * largest box the pane allows at this ratio, so the box hugs the picture:
   * no crop, no stretch, and no coloured surface spilling around a
   * transparent PNG or GIF.
   *
   * The ratio carries the source it was measured from rather than being reset
   * by an effect: the viewer opens on media the feed already cached, so `load`
   * can fire in the commit that set the source, ahead of passive effects, and a
   * reset would discard the measurement that had just arrived.
   */
  const [mediaAspect, setMediaAspect] = useState<{ uri: string; ratio: number } | null>(null);
  const readMediaAspect = useCallback((uri: string | null, width: number, height: number) => {
    if (!uri || width <= 0 || height <= 0) return;
    const ratio = width / height;
    setMediaAspect(current =>
      current && current.uri === uri && current.ratio === ratio ? current : { uri, ratio },
    );
  }, []);
  /** Only the measurement of the media currently on screen may frame the pane. */
  const activeMediaAspect =
    mediaAspect && mediaAspect.uri === activeMedia ? mediaAspect.ratio : null;
  const measureImage = useCallback(
    (el: HTMLImageElement | null) => {
      if (!el || !el.complete) return;
      readMediaAspect(activeMedia, el.naturalWidth, el.naturalHeight);
    },
    [activeMedia, readMediaAspect],
  );
  const measureVideo = useCallback(
    (el: HTMLVideoElement | null) => {
      if (!el || el.readyState < 1) return;
      readMediaAspect(activeMedia, el.videoWidth, el.videoHeight);
    },
    [activeMedia, readMediaAspect],
  );

  const postAvatarUrl = typeof post.avatar === 'string'
    ? post.avatar
    : post.avatar && typeof post.avatar === 'object' && !Array.isArray(post.avatar)
      ? (post.avatar as { uri?: string }).uri
      : undefined;

  // Refs for scroll-to-comment (notification-driven navigation)
  const commentRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const scrollAttemptedRef = useRef(false);
  const [commentNotFound, setCommentNotFound] = useState(false);

  /* ── Load comments ─────────────────────────────────────────────────── */
  const loadComments = useCallback(async (showLoading = true) => {
    if (staticTrendingPost) {
      setComments([]);
      setLoadError(null);
      if (showLoading) setLoading(false);
      return;
    }
    if (showLoading) {
      setLoading(true);
      setLoadError(null);
    }
    const result = await feedService.getComments(post.id);
    if (result.success) {
      if (!showLoading) {
        setNewCommentIds(new Set(result.data.filter(c => !knownCommentIds.current.has(c.id)).map(c => c.id)));
      }
      knownCommentIds.current = new Set(result.data.map(c => c.id));
      setComments(result.data);
      // Auto-expand comments that have replies (same as mobile)
      setExpandedReplies(new Set(result.data.filter(c => (c.replies?.length ?? 0) > 0).map(c => c.id)));
    } else if (showLoading) {
      // Reported as a failure — an empty list here would read as "no comments".
      setComments([]);
      setLoadError(t('common.errors.generic'));
    }
    if (showLoading) setLoading(false);
  }, [post.id, staticTrendingPost, t]);

  useEffect(() => { loadComments().catch(() => undefined); }, [loadComments]);
  useEffect(() => { setMediaIndex(initialMediaIndex); }, [post.id, initialMediaIndex]);
  // Reset post-like state and scroll refs when post changes
  useEffect(() => {
    setPostLiked(post.liked);
    setPostLikeCount(post.likes);
    scrollAttemptedRef.current = false;
    setCommentNotFound(false);
  }, [post.id, post.liked, post.likes]);

  /* ── Post-level like (optimistic) ──────────────────────────────────── */
  const handlePostLike = useCallback(() => {
    requireAuth(() => {
      const nextLiked = !postLiked;
      setPostLiked(nextLiked);
      setPostLikeCount(c => c + (nextLiked ? 1 : -1));
      likePost(post.id).catch(() => {
        // Revert on failure
        setPostLiked(!nextLiked);
        setPostLikeCount(c => c + (nextLiked ? -1 : 1));
      });
    });
  }, [likePost, post.id, postLiked, requireAuth]);

  /* ── Post-level share ──────────────────────────────────────────────── */
  const handlePostShare = useCallback(() => {
    requireAuth(() => {
      const shareUrl = `${window.location.origin}/?post=${post.id}`;
      if (navigator.share) {
        navigator.share({ title: post.title || 'Shared post', url: shareUrl }).catch(() => undefined);
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(shareUrl).then(() => {
          ToastService.show({ type: 'success', message: t('common.toast.copied') });
        }).catch(() => undefined);
      }
    });
  }, [post.id, post.title, requireAuth, t]);

  /* ── Scroll highlighted comment into view (notification navigation) ──── */
  useEffect(() => {
    if (!highlightCommentId || loading || scrollAttemptedRef.current) return;

    // Find the comment — it may be a top-level comment or a reply
    let foundCommentId: string | null = null;
    let foundParentId: string | null = null;

    for (const comment of comments) {
      if (comment.id === highlightCommentId || String(comment.postCommentId) === highlightCommentId) {
        foundCommentId = comment.id;
        break;
      }
      const reply = (comment.replies ?? []).find(
        r => r.id === highlightCommentId || String(r.postCommentId) === highlightCommentId,
      );
      if (reply) {
        foundCommentId = reply.id;
        foundParentId = comment.id;
        break;
      }
    }

    if (!foundCommentId) {
      setCommentNotFound(true);
      return;
    }

    // If the target is a reply, expand the parent's replies first
    if (foundParentId) {
      setExpandedReplies(prev => {
        const next = new Set(prev);
        next.add(foundParentId!);
        return next;
      });
    }

    // Wait for DOM to update (replies may need to render), then scroll
    const scrollTimer = window.setTimeout(() => {
      const el = commentRefs.current.get(foundCommentId!);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedCommentId(foundCommentId);
        if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = window.setTimeout(() => {
          setHighlightedCommentId(null);
          highlightTimerRef.current = null;
        }, 2500);
      }
      scrollAttemptedRef.current = true;
    }, 150);

    return () => window.clearTimeout(scrollTimer);
  }, [highlightCommentId, comments, loading]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    };
  }, []);

  // An inserted mention rewrites the whole value, so the caret has to be put
  // back after the composer re-renders.
  useEffect(() => {
    if (pendingCaret == null) return;
    const input = composerRef.current;
    input?.focus();
    input?.setSelectionRange(pendingCaret, pendingCaret);
    setPendingCaret(null);
  }, [pendingCaret]);

  useEffect(() => {
    if (newCommentIds.size === 0) return;
    const timeout = window.setTimeout(() => setNewCommentIds(new Set()), 700);
    return () => window.clearTimeout(timeout);
  }, [newCommentIds]);

  const closeMenu = useCallback(() => setMenu(null), []);

  /* ── Send comment (with optional parentId for replies) ─────────────── */
  const sendComment = useCallback(async (value: string, media?: MediaFile, parentId?: string) => {
    if (sending) return;
    setSending(true);
    setSendError('');
    const result = await feedService.createComment(post.id, value, parentId, media);
    if (result.success) {
      setText('');
      setReplyTarget(null);
      onCommentAdded();
      await loadComments(false);
    } else {
      setSendError(t('comments.errors.sendFailed'));
    }
    setSending(false);
  }, [loadComments, onCommentAdded, post.id, sending, t]);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMentionQuery(null);
    let value = text.trim();
    if (!value) return;
    // Normalize duplicate adjacent reply mentions: if the user manually
    // typed the same @mention that was auto-inserted by startReply, collapse
    // the pair into one so the stored reply shows the mention only once.
    if (replyTarget) {
      const mention = `@${commenterMentionToken(replyTarget)}`;
      const doubled = `${mention} ${mention}`;
      if (value.toLowerCase().startsWith(doubled.toLowerCase())) {
        value = value.slice(mention.length).trimStart();
      }
    }
    const parentId = resolveReplyParentId(comments, replyTarget);
    await sendComment(value, undefined, parentId);
  };

  const handleGifSelect = async (gif: GifSelection) => {
    setShowGifPicker(false);
    const parentId = resolveReplyParentId(comments, replyTarget);
    await sendComment(text.trim(), {
      uri: gif.uri,
      mediaType: POST_MEDIA_TYPE.GIF,
      type: gif.type,
      fileName: gif.fileName,
      fileSize: gif.fileSize,
    }, parentId);
  };

  const toggleEmojiPicker = () => {
    setShowGifPicker(false);
    setShowEmojiPicker(v => !v);
  };

  const toggleGifPicker = () => {
    setShowEmojiPicker(false);
    setShowGifPicker(v => !v);
  };

  /* ── Like ──────────────────────────────────────────────────────────── */
  const toggleLike = async (comment: CommentItem, parentId?: string) => {
    const patch = (item: CommentItem) =>
      item.id === comment.id
        ? { ...item, isLiked: !item.isLiked, likeCount: item.likeCount + (item.isLiked ? -1 : 1) }
        : item;

    setComments(prev => prev.map(c => {
      if (parentId && c.id === parentId) {
        return { ...c, replies: (c.replies ?? []).map(patch) };
      }
      if (!parentId && c.id === comment.id) return patch(c);
      return c;
    }));

    await feedService.toggleCommentLike(comment.id, comment.postCommentId);
  };

  /* ── Delete ────────────────────────────────────────────────────────── */
  const handleDeleteComment = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const backendId = deleteTarget.postCommentId ? String(deleteTarget.postCommentId) : deleteTarget.id;
    const result = await feedService.deleteComment(backendId);
    setDeleting(false);
    if (!result.success) {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
      return;
    }
    setComments(prev => prev.map(c => ({
      ...c,
      replies: (c.replies ?? []).filter(r => r.id !== deleteTarget.id),
    })).filter(c => c.id !== deleteTarget.id));
    onCommentCountChange?.(-1);
    setDeleteTarget(null);
  };

  /* ── Edit ──────────────────────────────────────────────────────────── */
  const startEdit = (comment: CommentItem) => {
    // The row keeps its kebab while its editor is open, so Edit can be chosen
    // again for the row already being edited — resetting the draft would throw
    // away what has been typed.
    if (editTarget?.id === comment.id) return;
    setEditTarget(comment);
    setEditText(comment.comment ?? '');
  };

  const cancelEdit = () => {
    setEditTarget(null);
    setEditText('');
  };

  const handleSaveEdit = async () => {
    if (!editTarget || editSaving) return;
    const trimmed = editText.trim();
    if (!trimmed) return;
    setEditSaving(true);
    const backendId = editTarget.postCommentId ? String(editTarget.postCommentId) : editTarget.id;
    const result = await feedService.editComment(backendId, trimmed);
    setEditSaving(false);
    if (!result.success) {
      ToastService.show({ type: 'error', message: t('comments.editFailed') });
      return;
    }
    const patch = (item: CommentItem) =>
      item.id === editTarget.id ? { ...item, comment: trimmed } : item;
    setComments(prev => prev.map(c => ({
      ...c,
      replies: (c.replies ?? []).map(patch),
    })).map(patch));
    setEditedCommentIds(prev => new Set(prev).add(editTarget.id));
    ToastService.show({ type: 'success', message: t('comments.editSuccess') });
    setEditTarget(null);
    setEditText('');
  };

  /* ── Reply ─────────────────────────────────────────────────────────── */
  const startReply = (comment: CommentItem) => {
    cancelEdit();
    setReplyTarget(comment);
    // Insert the @mention directly into the composer so it is visible,
    // editable, and uses the same clickable mention rendering as manually
    // typed ones. The mention is NOT prepended again during rendering or
    // submission — the text is the single source of truth.
    const mention = `@${commenterMentionToken(comment)} `;
    setText(mention);
    setPendingCaret(mention.length);
  };

  /* ── Replies toggle ────────────────────────────────────────────────── */
  const toggleReplies = useCallback((commentId: string) => {
    setExpandedReplies(prev => {
      const next = new Set(prev);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  }, []);

  /* ── Block ─────────────────────────────────────────────────────────── */
  const startBlock = (comment: CommentItem) => {
    if (!comment.authorId) return;
    setBlockTarget({
      userId: Number(comment.authorId),
      username: comment.username,
      isBlocked: !!comment.isBlocked,
    });
  };

  const handleConfirmBlock = async () => {
    if (!blockTarget) return;
    setBlockLoading(true);
    const shouldBlock = !blockTarget.isBlocked;
    const result = await profileService.setBlockRelation(blockTarget.userId, shouldBlock);
    setBlockLoading(false);
    if (result.success) {
      setComments(prev => prev.map(c => {
        const matches = c.authorId && String(c.authorId) === String(result.relatedUserId);
        const updated = matches ? { ...c, isBlocked: shouldBlock } : c;
        if (updated.replies) {
          return {
            ...updated,
            replies: updated.replies.map(r =>
              r.authorId && String(r.authorId) === String(result.relatedUserId)
                ? { ...r, isBlocked: shouldBlock }
                : r,
            ),
          };
        }
        return updated;
      }));
      toggleBlockUser(String(result.relatedUserId), blockTarget.username, shouldBlock);
      setBlockTarget(null);
    } else {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
    }
  };

  /* ── Report ────────────────────────────────────────────────────────── */
  const startReport = (comment: CommentItem) => {
    if (comment.profileUserGuid) {
      setReportTargetGuid(comment.profileUserGuid);
    }
  };

  /* ── Helpers ───────────────────────────────────────────────────────── */
  const isOwnComment = (comment: CommentItem): boolean => {
    if (comment.authorId && userId != null) return String(comment.authorId) === String(userId);
    return false;
  };

  /* ── Kebab menu contents ───────────────────────────────────────────── */
  const commentMenuItems: PopoverMenuItem<CommentMenuKey>[] = !menu
    ? []
    : isOwnComment(menu.comment)
      ? [
          { key: 'edit', label: t('comments.edit'), icon: PencilIcon },
          { key: 'delete', label: t('comments.delete'), icon: TrashIcon, destructive: true },
        ]
      : [
          { key: 'reply', label: t('comments.reply'), icon: ReplyIcon },
          { key: 'report', label: t('comments.report'), icon: AlertCircleIcon },
          {
            key: 'block',
            label: t(menu.comment.isBlocked ? 'home.postOptions.unblockUser' : 'home.postOptions.blockUser'),
            icon: BlockIcon,
            destructive: true,
          },
        ];

  const handleCommentMenuSelect = (key: CommentMenuKey) => {
    const comment = menu?.comment;
    if (!comment) return;
    closeMenu();
    switch (key) {
      case 'edit':
        startEdit(comment);
        break;
      case 'delete':
        setDeleteTarget(comment);
        break;
      case 'reply':
        startReply(comment);
        break;
      case 'report':
        startReport(comment);
        break;
      case 'block':
        startBlock(comment);
        break;
    }
  };

  /* ── Comment row renderer ──────────────────────────────────────────── */
  const renderCommentRow = (comment: CommentItem, isReply = false, parentId?: string) => {
    const isEditing = editTarget?.id === comment.id;
    const replyCount = comment.replies?.length ?? 0;
    const hasReplies = replyCount > 0;
    const isExpanded = expandedReplies.has(comment.id);
    const toggleLabel = isExpanded
      ? t('comments.hideReplies')
      : replyCount === 1
        ? t('comments.viewReply', { count: replyCount })
        : t('comments.viewReplies', { count: replyCount });

    const commentFallback = avatarFallback({
      seed: comment.profileUserGuid ?? comment.authorId,
      name: commenterName(comment),
      username: comment.username,
    });
    const commentAvatarEl = comment.avatar ? (
      <img className={`comment-row__avatar${isReply ? ' comment-row__avatar--reply' : ''}`} src={comment.avatar} alt="" />
    ) : (
      <span
        className={`comment-row__avatar comment-row__avatar--initials${isReply ? ' comment-row__avatar--reply' : ''}`}
        data-avatar-tone={commentFallback.tone}
        aria-hidden
      >
        {commentFallback.initial}
      </span>
    );
    const canOpenCommenter = Boolean(normalizeUserGuid(comment.profileUserGuid));

    return (
      <div
        key={comment.id}
        ref={el => {
          if (el) commentRefs.current.set(comment.id, el);
          else commentRefs.current.delete(comment.id);
        }}
        className={`comment-row-wrapper${isReply ? ' comment-row-wrapper--reply' : ''}${highlightedCommentId === comment.id ? ' comment-row-wrapper--highlighted' : ''}`}
      >
        <article className={`comment-row${newCommentIds.has(comment.id) ? ' comment-row--new' : ''}${isReply ? ' comment-row--reply' : ''}`}>
          <div className="comment-row__avatar-col">
            {canOpenCommenter ? (
              <button
                className="comment-row__avatar-action"
                type="button"
                aria-label={t('comments.accessibility.openUserProfile', { username: comment.username })}
                onClick={() => requireAuth(() => openUserProfile(comment.profileUserGuid))}
              >
                {commentAvatarEl}
              </button>
            ) : (
              commentAvatarEl
            )}
          </div>
          <div className="comment-row__content">
            <div className="comment-row__meta">
              {normalizeUserGuid(comment.profileUserGuid) ? (
                <button
                  className="comment-row__author-action"
                  type="button"
                  aria-label={t('comments.accessibility.openUserProfile', { username: comment.username })}
                  onClick={() => requireAuth(() => openUserProfile(comment.profileUserGuid))}
                >
                  {commenterName(comment)}
                </button>
              ) : (
                <strong>{commenterName(comment)}</strong>
              )}
              <small>{formatApiTimestamp(comment.commentDate, { fallback: comment.createdAt, naiveZone: 'local' })}</small>
              {editedCommentIds.has(comment.id) && (
                <span className="comment-row__edited-label">{t('comments.edited')}</span>
              )}
            </div>
            {isEditing ? (
              <div className="comment-row__edit">
                <textarea
                  className="comment-row__edit-input"
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  placeholder={t('comments.editPlaceholder')}
                  aria-label={t('comments.editPlaceholder')}
                  rows={2}
                  maxLength={COMMENT_CHAR_LIMIT}
                />
                <div className="comment-row__edit-footer">
                  <span className="comment-row__edit-counter">{editText.length}/{COMMENT_CHAR_LIMIT}</span>
                  <div className="comment-row__edit-actions">
                    <button type="button" className="comment-row__edit-cancel" onClick={cancelEdit}>{t('comments.cancelEdit')}</button>
                    <button type="button" className="comment-row__edit-save" onClick={() => { handleSaveEdit().catch(() => undefined); }} disabled={editSaving || !editText.trim() || editText.trim() === (editTarget?.comment ?? '').trim() || editText.length > COMMENT_CHAR_LIMIT}>
                      <SendIcon size={14} />
                      {t('comments.saveEdit')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {comment.comment && (
                  <p>
                    {!!comment.replyTo && !comment.comment.trimStart().toLowerCase().startsWith(`@${toMentionToken(comment.replyTo).toLowerCase()}`) && (
                      <span className="comment-row__reply-mention">@{comment.replyTo} </span>
                    )}
                    <MentionText text={comment.comment} mentions={mentions} />
                  </p>
                )}
              </>
            )}
            {/* Image stays visible during edit — only the text field is replaced */}
            {comment.image && <img className="comment-row__media" src={comment.image} alt={t('comments.commentImage')} loading="lazy" />}
            {/* Mobile exposes Reply for both root comments and nested replies. */}
            {!isEditing && (
              <button type="button" className="comment-row__reply-btn" onClick={() => startReply(comment)}>
                {t('comments.reply')}
              </button>
            )}
          </div>
          {/* Right column: kebab on top (revealed on row hover/focus), like below.
              Both stay mounted during edit so the row never reflows. */}
          <div className="comment-row__right-col">
            <button
              className="comment-row__kebab"
              type="button"
              onClick={(e) => {
                setMenu({ comment, anchorRect: e.currentTarget.getBoundingClientRect() });
              }}
              aria-label={t('comments.commentOptions')}
              aria-expanded={menu?.comment.id === comment.id}
              aria-haspopup="dialog"
            >
              <PostMenuIcon size={16} />
            </button>
            <button
              className={`comment-row__like${comment.isLiked ? ' comment-row__like--active' : ''}`}
              type="button"
              onClick={() => { toggleLike(comment, parentId).catch(() => undefined); }}
              aria-label={t('comments.likeComment')}
            >
              <HeartIcon size={14} fill={comment.isLiked ? 'currentColor' : 'none'} />
              <span>{comment.likeCount}</span>
            </button>
          </div>
        </article>

        {/* View / Hide replies toggle */}
        {hasReplies && !isReply && (
          <div className="comment-row__replies-section">
            <button type="button" className="comment-row__replies-toggle" onClick={() => toggleReplies(comment.id)}>
              {toggleLabel}
            </button>
            {isExpanded && (
              <div className="comment-row__replies-list">
                {(comment.replies ?? []).map(reply => renderCommentRow(reply, true, comment.id))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Shared JSX fragments used by both layouts
  const canOpenPostAuthor = Boolean(normalizeUserGuid(post.profileUserGuid));
  const handleOpenPostAuthor = () => requireAuth(() => openUserProfile(post.profileUserGuid));
  const postAuthorFallback = avatarFallback({
    seed: post.profileUserGuid ?? post.userId,
    name: post.username,
    username: post.username,
  });
  const postAuthorAvatarEl = postAvatarUrl ? (
    <img className="comments-modal__post-author-avatar" src={postAvatarUrl} alt="" />
  ) : (
    <span
      className="comments-modal__post-author-avatar comments-modal__post-author-avatar--initials"
      data-avatar-tone={postAuthorFallback.tone}
      aria-hidden
    >
      {postAuthorFallback.initial}
    </span>
  );

  const postAuthor = (
    <div className="comments-modal__post-author">
      {canOpenPostAuthor ? (
        <button
          className="comments-modal__post-author-avatar-action"
          type="button"
          aria-label={t('home.accessibility.openUserProfile', { username: post.username })}
          onClick={handleOpenPostAuthor}
        >
          {postAuthorAvatarEl}
        </button>
      ) : (
        postAuthorAvatarEl
      )}
      {canOpenPostAuthor ? (
        <button className="comments-modal__post-author-action" type="button" onClick={handleOpenPostAuthor}>
          {getUsernameDisplay(post)}
        </button>
      ) : (
        <strong>{getUsernameDisplay(post)}</strong>
      )}
      <small>{formatApiTimestamp(post.postDate, { fallback: post.timeAgo, naiveZone: 'local' })}</small>
      {onMenu && (
        <button
          className="comments-modal__post-menu"
          type="button"
          aria-label={t('home.rightBanner.more')}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            onMenu(post, rect);
          }}
        >
          <PostMenuIcon size={18} />
        </button>
      )}
    </div>
  );

  const { caption: postCaption, separateHashtags: postSeparateHashtags } = useMemo(
    () => splitSeparateHashtags(post.text, post.hashtags),
    [post.text, post.hashtags],
  );

  const postText = (
    <>
      {postCaption.trim() && (
        containsHtml(postCaption)
          ? <HtmlContent html={postCaption} className="comments-modal__post-text" mentions={mentions} />
          : <p className="comments-modal__post-text"><MentionText text={postCaption} mentions={mentions} /></p>
      )}
      {postSeparateHashtags.length > 0 && (
        <p className="comments-modal__post-text comments-modal__separate-hashtags">
          {postSeparateHashtags.map(tag => <Hashtag key={tag}>{tag}</Hashtag>)}
        </p>
      )}
    </>
  );

  const mediaStage = activeMedia && (
    <div className="comments-modal__media-stage">
      {videoUri ? (
        <video
          ref={measureVideo}
          className="comments-modal__post-media"
          src={activeMedia}
          controls
          preload="metadata"
          onLoadedMetadata={event => measureVideo(event.currentTarget)}
        />
      ) : (
        <img
          ref={measureImage}
          className="comments-modal__post-media"
          src={activeMedia}
          alt=""
          onLoad={event => measureImage(event.currentTarget)}
        />
      )}
      {mediaSources.length > 1 && <div className="comments-modal__media-navigation"><button type="button" onClick={() => setMediaIndex(i => i === 0 ? mediaSources.length - 1 : i - 1)} aria-label={t('home.feed.actions.previousMedia')}>‹</button><button type="button" onClick={() => setMediaIndex(i => (i + 1) % mediaSources.length)} aria-label={t('home.feed.actions.nextMedia')}>›</button></div>}
    </div>
  );

  const postActions = (
    <footer className="comments-modal__post-actions">
      <div className="comments-modal__post-action-group">
        <button
          className={`comments-modal__post-action${postLiked ? ' comments-modal__post-action--liked' : ''}`}
          type="button"
          onClick={handlePostLike}
          aria-pressed={postLiked}
          aria-label={t('home.feed.actions.like')}
        >
          <HeartIcon size={20} fill={postLiked ? 'currentColor' : 'none'} />
        </button>
        {postLikeCount > 0 && (
          <button
            className="comments-modal__post-count"
            type="button"
            onClick={() => requireAuth(() => setLikesModalOpen(true))}
            aria-label={t('home.feed.actions.viewLikes')}
          >
            {postLikeCount}
          </button>
        )}
      </div>
      <div className="comments-modal__post-action-group">
        <span className="comments-modal__post-action" aria-label={`${t('home.feed.actions.comment')} (${post.comments})`}>
          <CommentIcon size={20} />
          <span>{post.comments}</span>
        </span>
      </div>
      {canSharePost(post) && (
        <div className="comments-modal__post-action-group">
          <button
            className="comments-modal__post-action"
            type="button"
            onClick={handlePostShare}
            aria-label={`${t('home.feed.actions.share')} (${post.shares})`}
          >
            <ShareIcon size={20} />
            <span>{post.shares}</span>
          </button>
        </div>
      )}
    </footer>
  );

  const commentsList = (
    <div className="comments-modal__list" aria-live="polite">
      {staticTrendingPost ? (
        <ListStatus
          variant="empty"
          layout="inline"
          title={t('comments.guestTitle')}
          message={t('comments.guestMessage')}
          onAction={() => requireAuth()}
          actionLabel={t('auth.gate.logIn')}
        />
      ) : loading ? (
        <FeedUserRowsSkeleton />
      ) : loadError ? (
        <ListStatus variant="error" layout="inline" message={loadError} onAction={() => { loadComments().catch(() => undefined); }} />
      ) : comments.length === 0 ? (
        <ListStatus variant="empty" layout="inline" title={t('comments.noComments')} />
      ) : comments.map(comment => renderCommentRow(comment))}
      {commentNotFound && !loading && (
        <p className="comments-modal__comment-unavailable" role="status">{t('comments.commentNotFound')}</p>
      )}
    </div>
  );

  const pickersAndBanner = (
    <>
      {showGifPicker && (
        <div className="comments-modal__gif-panel">
          <GifPicker onSelect={gif => { handleGifSelect(gif).catch(() => undefined); }} disabled={sending} />
        </div>
      )}
      {showEmojiPicker && (
        <div className="comments-modal__emoji-panel">
          <EmojiPicker onSelect={emoji => setText(v => `${v}${emoji}`)} />
        </div>
      )}
      {(replyTarget || editTarget) && (
        <div className="comments-modal__banner">
          <span>
            {editTarget ? t('comments.editing') : t('comments.replying')}
            {!editTarget && replyTarget && (
              <strong> @{commenterName(replyTarget)}</strong>
            )}
          </span>
          <button type="button" onClick={() => { setReplyTarget(null); cancelEdit(); }} aria-label={t('common.buttons.close')}>×</button>
        </div>
      )}
    </>
  );

  /* ── @mention autocomplete ─────────────────────────────────────────── */
  const activeMentionIndex = mentionSuggestions.length
    ? Math.min(mentionIndex, mentionSuggestions.length - 1)
    : 0;
  const mentionListOpen = mentionSuggestions.length > 0;
  const mentionOptionId = (index: number) => `${MENTION_LISTBOX_ID}-option-${index}`;

  /** Re-reads the `@…` under the caret after any edit or caret move. */
  const syncMentionQuery = (input: HTMLTextAreaElement) => {
    const next = readMentionQuery(input.value, input.selectionStart);
    setMentionQuery(previous =>
      previous && next && previous.start === next.start && previous.query === next.query
        ? previous
        : next,
    );
    setMentionIndex(0);
  };

  const selectMention = (suggestion: MentionSuggestion) => {
    const input = composerRef.current;
    if (!input || !mentionQuery) return;
    const inserted = applyMentionSelection(input.value, mentionQuery, suggestion.token);
    setText(inserted.value);
    setPendingCaret(inserted.caret);
    setMentionQuery(null);
  };

  const handleComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!mentionListOpen) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMentionIndex((activeMentionIndex + 1) % mentionSuggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionIndex(
        (activeMentionIndex - 1 + mentionSuggestions.length) % mentionSuggestions.length,
      );
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      selectMention(mentionSuggestions[activeMentionIndex]);
    } else if (event.key === 'Escape') {
      // Dismiss the list without letting the modal read the same Escape.
      event.preventDefault();
      event.stopPropagation();
      setMentionQuery(null);
    }
  };

  const composer = staticTrendingPost ? null : (
    <form className="comments-modal__composer" onSubmit={submit}>
      {mentionListOpen && (
        <MentionSuggestions
          suggestions={mentionSuggestions}
          activeIndex={activeMentionIndex}
          listboxId={MENTION_LISTBOX_ID}
          optionId={mentionOptionId}
          onSelect={selectMention}
          onActiveIndexChange={setMentionIndex}
        />
      )}
      {sendError && <FormError variant="banner" className="comments-modal__error" message={sendError} />}
      <button className="comments-modal__tool" type="button" onClick={toggleEmojiPicker} aria-pressed={showEmojiPicker} aria-label={t('comments.emojiPicker')}><SmileIcon size={20} /></button>
      <button className="comments-modal__tool comments-modal__tool--gif" type="button" onClick={toggleGifPicker} aria-pressed={showGifPicker} aria-label={t('comments.gifPicker')}>{t('comments.gif')}</button>
      <textarea
        ref={composerRef}
        value={text}
        onChange={event => {
          setText(event.target.value);
          syncMentionQuery(event.target);
        }}
        onClick={event => syncMentionQuery(event.currentTarget)}
        onKeyUp={event => {
          if (isCaretMoveKey(event.key)) syncMentionQuery(event.currentTarget);
        }}
        onBlur={() => setMentionQuery(null)}
        onKeyDown={handleComposerKeyDown}
        placeholder={editTarget ? t('comments.editPlaceholder') : replyTarget ? t('comments.reply') : t('comments.placeholder')}
        aria-label={t('comments.placeholder')}
        role="combobox"
        aria-expanded={mentionListOpen}
        aria-controls={mentionListOpen ? MENTION_LISTBOX_ID : undefined}
        aria-activedescendant={mentionListOpen ? mentionOptionId(activeMentionIndex) : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        rows={1}
        maxLength={COMMENT_CHAR_LIMIT}
      />
      <button className="comments-modal__send" type="submit" disabled={sending || !text.trim()} aria-label={t('comments.send')}><CommentIcon size={18} /></button>
    </form>
  );

  const subModals = (
    <>
      {menu && (
        <PopoverMenu
          anchorRect={menu.anchorRect}
          items={commentMenuItems}
          label={t('comments.accessibility.commentMenu')}
          labelledBy="comment-menu-title"
          onClose={closeMenu}
          onSelect={handleCommentMenuSelect}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          title={t('comments.deleteConfirmTitle')}
          message={t('comments.deleteConfirmMessage')}
          confirmLabel={t('comments.deleteConfirm')}
          cancelLabel={t('comments.deleteCancel')}
          loading={deleting}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => { handleDeleteComment().catch(() => undefined); }}
        />
      )}
      {blockTarget && (
        <BlockUserModal
          username={commenterName(blockTarget)}
          seed={blockTarget.userId}
          isBlocked={blockTarget.isBlocked}
          loading={blockLoading}
          onClose={() => setBlockTarget(null)}
          onConfirm={() => { handleConfirmBlock().catch(() => undefined); }}
        />
      )}
      {reportTargetGuid && (
        <ReportUserModal
          userGuid={reportTargetGuid}
          onClose={() => setReportTargetGuid(null)}
        />
      )}
      {likesModalOpen && (
        <LikesModal postId={post.id} onClose={() => setLikesModalOpen(false)} />
      )}
    </>
  );

  const modalClass = mediaViewer
    ? 'feed-modal--media-viewer'
    : fullView
      ? 'feed-modal--post-detail'
      : '';

  return (
    <Modal title={fullView ? title || post.title || t('postViewer.titleWithUsername', { username: getUsernameDisplay(post) }) : t('comments.title')} onClose={onClose} labelledBy="comments-modal-title" className={modalClass}>
      {mediaViewer ? (
        /* ── Split media-viewer layout: media left, post+comments right ── */
        <div className="media-viewer">
          <div
            className={`media-viewer__media${activeMediaAspect ? ' media-viewer__media--framed' : ''}`}
            style={activeMediaAspect ? ({ '--media-aspect': String(activeMediaAspect) } as CSSProperties) : undefined}
          >
            {mediaStage}
          </div>
          <div className="media-viewer__side">
            <div className="media-viewer__scroll">
              <article className="comments-modal__post">
                {postAuthor}
                {postText}
                {postActions}
              </article>
              {commentsList}
            </div>
            {pickersAndBanner}
            {composer}
          </div>
        </div>
      ) : (
        /* ── Stacked layout: post → comments (existing behavior) ──────── */
        <>
          <div className="comments-modal__body">
            <article className="comments-modal__post">
              {postAuthor}
              {postText}
              {mediaStage}
              {postActions}
            </article>
            {commentsList}
          </div>
          {pickersAndBanner}
          {composer}
        </>
      )}
      {subModals}
    </Modal>
  );
}
